import React, { useCallback, useEffect, useState } from 'react';
import { sendAssistantMessage } from '../../api/ai';
import type { AssistantStreamCallbacks } from '../../api/ai';
import QuipaAssistantModal from './quipa-assistant-modal';
import {
  dispatchQuipaAssistantClosed,
  QUIPA_ASSISTANT_OPEN_EVENT,
  QuipaAssistantContext,
} from './quipa-assistant-events';

const isAbortError = (error: unknown) =>
  error instanceof DOMException
    ? error.name === 'AbortError'
    : !!(error && typeof error === 'object' && (error as any).name === 'AbortError');

const QuipaAssistantLauncher: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [launchContext, setLaunchContext] = useState<QuipaAssistantContext>({});

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOpen = (event: Event) => {
      const customEvent = event as CustomEvent<{ context?: QuipaAssistantContext }>;
      setLaunchContext(customEvent.detail?.context || {});
      setIsOpen(true);
    };

    window.addEventListener(QUIPA_ASSISTANT_OPEN_EVENT, handleOpen as EventListener);
    return () => window.removeEventListener(QUIPA_ASSISTANT_OPEN_EVENT, handleOpen as EventListener);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    dispatchQuipaAssistantClosed(launchContext);
    setLaunchContext({});
  }, [launchContext]);

  const handleSend = useCallback(
    async (
      message: string,
      sessionId: string,
      callbacks?: Pick<AssistantStreamCallbacks, 'onStatus' | 'onMessage'>,
      signal?: AbortSignal
    ) => {
      const trimmed = message.trim();
      if (!trimmed) return 'Please provide a message for the assistant.';

      const extractAssistantText = (res: any) => {
        if (!res) return '';
        if (typeof res === 'string') return res;
        if (typeof res.message === 'string' && res.message.trim()) return res.message;
        if (typeof res.content === 'string' && res.content.trim()) return res.content;
        if (typeof res.reply === 'string' && res.reply.trim()) return res.reply;
        return '';
      };

      const formatReplyTime = () =>
        new Date().toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        });

      const accountId = parseFloat(localStorage.getItem('accountId') || '0') || undefined;

      try {
        const res = await sendAssistantMessage({
          message: trimmed,
          sessionId,
          accountId,
          context: {
            ...launchContext,
            pathname: window.location.pathname,
          },
        }, {
          ...callbacks,
          signal,
        });

        const reply = extractAssistantText(res);
        if (reply) {
          return {
            id: `assistant-${Date.now()}`,
            sender: 'assistant' as const,
            text: reply,
            timestamp: formatReplyTime(),
            metadata: res && typeof res === 'object' ? res : null,
            sessionId,
          };
        }
        if (res?.wasStreamed) return undefined;
        if (res && res.error) return res.error;
        return 'Thanks! Your request was sent to QUIPA Assistant.';
      } catch (error) {
        if (isAbortError(error)) return undefined;
        console.error('Assistant request failed', error);
        if (error instanceof Error && error.message) {
          return error.message;
        }
        return 'We could not reach QUIPA Assistant. Please try again.';
      }
    },
    [launchContext]
  );

  return (
    <QuipaAssistantModal
      isOpen={isOpen}
      onClose={handleClose}
      onSend={handleSend}
      launchContext={launchContext}
    />
  );
};

export default QuipaAssistantLauncher;
